import React, { useState } from 'react';
import AvItemWithLogoGroup, { IconSize } from '@components/AvItemWithLogoGroup';
import { IconVariant } from '@components/ItemWithLogo';
import { NoDataFoundWidget } from '@components/Widgets/layout.components';
import { AvTooltip } from '@fabric-ui/components';
import { ellipsis, flex } from '@fabric-ui/theme';
import { abbreviateNumber } from '@fabric-ui/utils';
import { Box, Skeleton, Typography } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import useQueryObject from '../../../../hooks/useQueryObjectSql';
import { getTop5VendorsByAlerts, getTop5VendorsByIncidents } from '../queries';
import { FilterContext, SelectedWidgetData, TableDataType } from '../types';
import WidgetTooltip, { TooltipDivider } from './WidgetTooltip';

interface Top5VendorsByAlertsVsIncidentsProps {
  selected?: SelectedWidgetData;
  onSelect?: (selected: SelectedWidgetData) => void;
  closeInteractive?: () => void;
  filterContext: FilterContext;
}

const Top5VendorsByAlertsVsIncidents = (props: Top5VendorsByAlertsVsIncidentsProps) => {
  const { filterContext, onSelect, selected, closeInteractive } = props;

  const [currentSelect, setCurrentSelect] = useState<SelectedWidgetData | undefined>(selected);

  const { data: incidentData = [], isLoading: isLoadingIncident } = useQueryObject({
    queryObject: [getTop5VendorsByIncidents(filterContext)],
    options: { placeholderData: keepPreviousData },
  });

  const { data: alertData = [], isLoading: isLoadingAlert } = useQueryObject({
    queryObject: [getTop5VendorsByAlerts(filterContext)],
    options: { placeholderData: keepPreviousData },
  });

  const data = React.useMemo(() => {
    if (isLoadingAlert || isLoadingIncident) {
      return [];
    }

    const mergedData: { [key: string]: { name: string; active_incidents_granular: number; active_alerts_granular: number } } = {};

    incidentData.forEach(item => {
      item.source_names?.forEach(name => {
        if (name) {
          if (!mergedData[name]) {
            mergedData[name] = { name, active_incidents_granular: 0, active_alerts_granular: 0 };
          }
          mergedData[name].active_incidents_granular += item.active_incidents_granular || 0;
        }
      });
    });

    alertData.forEach(item => {
      item.source_names?.forEach(name => {
        if (name) {
          if (!mergedData[name]) {
            mergedData[name] = { name, active_incidents_granular: 0, active_alerts_granular: 0 };
          }
          mergedData[name].active_alerts_granular += item.active_alerts_granular || 0;
        }
      });
    });

    const allData = Object.values(mergedData);

    const sortedData = allData.sort((a, b) => {
      const totalA = a.active_alerts_granular + a.active_incidents_granular;
      const totalB = b.active_alerts_granular + b.active_incidents_granular;
      return totalB - totalA;
    });

    return sortedData.slice(0, 5);
  }, [incidentData, alertData, isLoadingAlert, isLoadingIncident]);

  if (!currentSelect && closeInteractive) {
    closeInteractive();
  }

  const isLoading = isLoadingAlert || isLoadingIncident;

  const isBarSelected = (barName: string, barType: TableDataType) =>
    currentSelect?.name === barName && currentSelect?.tableType === barType;

  const onBarClick = (v, tableType: TableDataType) => {
    if (v.name !== undefined) {
      const selected: SelectedWidgetData = {
        tableType,
        name: v.name,
        filter: { [tableType === TableDataType.ALERT ? 'alert.source_names' : 'incident.source_names']: [v.name] },
      };
      if (onSelect) {
        onSelect(selected);
      }
      // If the same bar is clicked again, deselect it. Otherwise, select the new one.
      setCurrentSelect(isBarSelected(v.name, tableType) ? undefined : selected);
    }
  };

  return (
    <Box sx={{ ...flex.col, gap: 1, padding: '16px 24px', minHeight: 250, height: '100%' }}>
      <Typography sx={{ whiteSpace: 'nowrap', fontSize: '18px' }} variant="h2">
        Top 5 Sources by Alerts vs Incidents
      </Typography>

      <Box sx={{ flex: 1, width: '100%' }}>
        {!isLoading ? (
          data.length === 0 ? (
            <Box sx={{ height: '100%', ...flex.row }}>
              <NoDataFoundWidget />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 0, right: 0, left: 0, bottom: 5 }} barSize={20}>
                <CartesianGrid vertical={false} stroke="var(--color-border-base-subdued)" />
                <XAxis dataKey="name" tickLine={false} axisLine={false} interval={0} height={35} tick={<CustomXAxisTick />} />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  stroke="var(--color-content-base-tertiary)"
                  tickFormatter={v => abbreviateNumber(v)}
                />
                <Tooltip cursor={{ fill: 'var(--color-surface-base-secondary)' }} content={<CustomTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{
                    top: -30,
                    left: 0,
                    fontSize: 12,
                    paddingTop: 30,
                  }}
                  payload={[
                    { value: 'Alerts', type: 'circle', color: 'var(--color-surface-base-brand)' },
                    { value: 'Incidents', type: 'circle', color: 'var(--color-cyan-500)' },
                  ]}
                  content={<CustomLegendContent />}
                />
                <Bar
                  style={{ cursor: 'pointer', outline: 'none', pointerEvents: 'all' }}
                  onClick={v => {
                    onBarClick(v, TableDataType.ALERT);
                  }}
                  dataKey="active_alerts_granular">
                  {data.map(_entry => (
                    <Cell
                      fill={
                        isBarSelected(_entry.name, TableDataType.ALERT) || !currentSelect
                          ? 'var(--color-surface-base-brand)'
                          : 'var(--color-surface-interactive-primary-default)'
                      }
                      key={`cell-${_entry.name}`}
                    />
                  ))}
                </Bar>
                <Bar
                  style={{ cursor: 'pointer', outline: 'none', pointerEvents: 'all' }}
                  onClick={v => {
                    onBarClick(v, TableDataType.INCIDENT);
                  }}
                  dataKey="active_incidents_granular">
                  {data.map(_entry => (
                    <Cell
                      fill={
                        isBarSelected(_entry.name, TableDataType.INCIDENT) || !currentSelect
                          ? 'var(--color-cyan-200)'
                          : 'var(--color-cyan-500)'
                      }
                      key={`cell-${_entry.name}`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )
        ) : (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        )}
      </Box>
    </Box>
  );
};

interface CustomLegendContentProps {
  payload?: Array<{
    value: string;
    id: string;
    type: 'circle' | 'rect' | 'line'; // or any custom type
    color: string;
    payload: any; // Original data payload for the item
  }>;
}

const CustomXAxisTick: React.FC<CustomXAxisTickProps> = ({ x, y = 0, payload, width = 0, visibleTicksCount = 1 }) => {
  if (!payload || !payload.value) {
    return null;
  }

  const vendorName = payload.value;
  const iconSize = 24;
  const foreignObjectWidth = iconSize + (width - iconSize) / visibleTicksCount;
  const foreignObjectHeight = iconSize + 10;
  const fontSize = 12; // Font size for the vendor name text

  return (
    <g style={{ pointerEvents: 'all', zIndex: 1 }} transform={`translate(${x},${y + 20})`}>
      <foreignObject
        // Position the foreignObject to be centered horizontally and vertically around the tick's (x,y)
        // 'y' from recharts is typically the center of the tick mark.
        x={-foreignObjectWidth / 2}
        y={-foreignObjectHeight / 2}
        width={foreignObjectWidth}
        height={foreignObjectHeight}>
        <Box
          sx={{
            ...flex.center,
            width: '100%',
            height: '100%',
            gap: 1,
          }}>
          <AvItemWithLogoGroup
            variant={IconVariant.sourcesMapByName}
            items={[vendorName]}
            size={IconSize.collapsed}
            shouldShowTooltip
            limit={1}
          />
          <AvTooltip
            sx={{
              color: 'var(--color-content-base-tertiary)',
              fontSize,
              ...ellipsis,
              maxWidth: '100%',
              textAlign: 'center',
            }}>
            {vendorName}
          </AvTooltip>
        </Box>
      </foreignObject>
    </g>
  );
};

const CustomLegendContent = (props: CustomLegendContentProps) => {
  const { payload } = props;
  const circleSize = 8;

  if (!payload || payload.length === 0) {
    return null;
  }

  return (
    <Box sx={{ ...flex.row, justifyContent: 'flex-start', marginBottom: '30px' }}>
      {payload.map(entry => (
        <Box key={`legend-item-${entry.id}${entry.value}`} sx={{ ...flex.itemsCenter, marginRight: '15px' }}>
          <Box
            sx={{
              width: circleSize,
              height: circleSize,
              borderRadius: '50%',
              backgroundColor: entry.color,
              marginRight: '4px',
            }}
          />
          <Box sx={{ fontSize: 12, color: 'var(--color-content-base-tertiary)' }}>{entry.value}</Box>
        </Box>
      ))}
    </Box>
  );
};

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value: string; // This will be the vendor name (e.g., "CrowdStrike Alerts")
    offset: number;
  };
  width?: number;
  visibleTicksCount?: number;
}

const CustomTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    const alertPayload = payload.find(p => p.dataKey === 'active_alerts_granular');
    const incidentPayload = payload.find(p => p.dataKey === 'active_incidents_granular');

    return (
      <WidgetTooltip active={active} payload={payload}>
        <Box sx={{ ...flex.justifyBetweenCenter }}>
          <Box sx={{ color: 'var(--color-content-immutable-white)', fontWeight: 600 }}>Source</Box>
          <Box sx={{ ...flex.itemsCenter }}>
            <AvItemWithLogoGroup
              sx={{ color: 'var(--color-content-base-tertiary)' }}
              variant={IconVariant.sourcesMapByName}
              items={[label]}
              size={IconSize.small}
              shouldShowTooltip={false}
              limit={1}
            />
          </Box>
        </Box>
        <TooltipDivider />
        <Box sx={{ ...flex.col, gap: 1 }}>
          {alertPayload && (
            <Box sx={{ ...flex.justifyBetweenCenter }}>
              <Box sx={{ color: 'var(--color-content-base-secondary)' }}>Alerts Count</Box>
              <Box sx={{ fontWeight: 600, color: 'var(--color-content-immutable-white)' }}>{alertPayload.value.toLocaleString()}</Box>
            </Box>
          )}
          {incidentPayload && (
            <Box sx={{ ...flex.justifyBetweenCenter }}>
              <Box sx={{ color: 'var(--color-content-base-secondary)' }}>Incidents Count</Box>
              <Box sx={{ fontWeight: 600, color: 'var(--color-content-immutable-white)' }}>{incidentPayload.value.toLocaleString()}</Box>
            </Box>
          )}
        </Box>
      </WidgetTooltip>
    );
  }

  return null;
};

export default Top5VendorsByAlertsVsIncidents;
